import { useTranslation, Trans } from 'react-i18next'
import styled from 'styled-components'
import CloseButton from '@/components/close-button'
import { Warning } from '@/components/icons'
import { createModal, useModalize } from '@/components/modalize'
import { breakpoints, colors, fontWeights } from '@/styles'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-height: 90vh;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    max-height: unset;
    max-height: calc(100% - 64px);
  }
`

const Header = styled.div`
  height: 64px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0px 25px 0;
  flex-shrink: 0;
  margin-bottom: 25px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    justify-content: flex-start;
    padding: 5px 20px 0;
  }
`

const ContentWrapper = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 24px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    padding: 0 16px;
  }
`

const ContentInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 760px;
  width: 100%;
`

const MultiParagraphContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 20px;
  width: 100%;
  margin-top: ${props => (props.top ? props.top : 0)}px;
`

const Title = styled.h1`
  font-size: 16px;
  line-height: 27px;
  font-weight: ${fontWeights.bold};
`

const Text = styled.span`
  color: ${colors.grey16};
  font-size: 14px;
  font-weight: ${fontWeights.regular};
  line-height: 20px;
  width: 100%;
`

const ButtonContainer = styled.div`
  width: 100%;
  height: 72px;
  margin-top: 24px;
  background: ${colors.grey96};
  display: grid;
  justify-content: flex-end;
  align-items: center;
  grid-template-columns: 100px 100px;
  grid-gap: 16px;
  padding: 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    justify-content: center;
    grid-template-columns: 1fr 1fr;
  }
`

const ContinueButton = styled.a`
  width: 100%;
  height: 100%;
  max-width: 100px;
  max-height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px 16px;
  font-size: 12px;
  font-weight: ${fontWeights.bold};
  color: ${colors.white};
  background: ${colors.blue16};
  box-shadow: 0px 2px 4px rgba(19, 43, 62, 0.25), 0px 6px 6px 2px rgba(19, 43, 62, 0.1);
  border-radius: 6px;
  cursor: pointer;
  :hover {
    opacity: 0.7;
  }
  @media screen and (max-width: ${breakpoints.phoneMax}) {
    max-width: unset;
  }
`

const CancelButton = styled(ContinueButton)`
  background: ${colors.white};
  color: ${colors.blue16};
`

const IconWrapper = styled.div`
  margin-right: 12px;
`

const Link = styled.a`
  color: ${colors.link};
  text-decoration: underline;
`

const ExternalLinkModal = ({ link }) => {
  const { t } = useTranslation()
  const { close } = useModalize()

  return (
    <Wrapper>
      <Header>
        <IconWrapper>
          <Warning />
        </IconWrapper>
        <Title>{t('b.chat.view.external_link_notice.title', 'External Link Notice')}</Title>
        <CloseButton top={21} right={21} onClick={close} />
      </Header>
      <ContentWrapper>
        <ContentInner>
          <MultiParagraphContainer>
            <Text>
              <Trans i18nKey="b.chat.view.external_link_text1.text">
                You are continuing to another website, which opens in a new tab, that liv.rent
                doesn’t own or operate. Its owner is solely responsible for the website’s content
                and level of security, and their policies may differ from liv.rent’s{' '}
                <Link target="_blank" href="https://liv.rent/terms">
                  Terms and Conditions
                </Link>{' '}
                and{' '}
                <Link target="_blank" href="https://liv.rent/privacy">
                  Privacy Policy
                </Link>
                .
              </Trans>
            </Text>
            <Text>
              {t(
                'b.chat.view.external_link_text2.text',
                'liv.rent is not responsible for any content on external websites.'
              )}
            </Text>
            <Text>{link}</Text>
          </MultiParagraphContainer>
        </ContentInner>
      </ContentWrapper>
      <ButtonContainer>
        <CancelButton onClick={close}>{t('common.cancel', 'Cancel')}</CancelButton>

        <ContinueButton href={link} target="_blank" rel="noopener noreferrer">
          {t('b.chat.view.continue.button', 'Continue')}
        </ContinueButton>
      </ButtonContainer>
    </Wrapper>
  )
}

export const show = createModal(ExternalLinkModal, {
  width: 'auto',
  maxWidth: '448px',
  flexGrow: true,
  border: '1px solid #d8d8d8',
  mobileAlign: 'flex-end',
  tabletFullWidth: true,
  mobileBorderRadius: '16px 16px 0 0'
})
